/**
 * MapFiltersBar — Floating overlay for filtering the ArcGIS map by tipo and date range.
 * Fetches its own filter-options data.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Filter, X, CalendarRange } from 'lucide-react';
import { Button } from './ui/button';
import FilterCombobox from './FilterCombobox';
import { tasksAPI } from '../lib/api';

export default function MapFiltersBar({ filters, onChange, onClose }) {
  const [local, setLocal] = useState(filters);

  const { data: optData, isLoading } = useQuery({
    queryKey: ['filter-options'],
    queryFn: () => tasksAPI.getFilterOptions().then(r => r.data),
    staleTime: 10 * 60 * 1000,
  });

  const tipos = optData?.tipos || [];

  const hasActive = !!(local.tipo || local.date_from || local.date_to);
  const activeCount = (local.tipo ? 1 : 0) + ((local.date_from || local.date_to) ? 1 : 0);

  const apply = () => onChange(local);
  const clear = () => { setLocal({ tipo: '', date_from: '', date_to: '' }); onChange({ tipo: '', date_from: '', date_to: '' }); };

  return (
    <div
      data-testid="map-filters-bar"
      className="absolute top-3 right-3 z-[100] bg-white/97 dark:bg-gray-900/97 backdrop-blur-md rounded-xl border border-border shadow-2xl p-4 w-68"
      style={{ width: '272px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-primary" />
          <h4 className="text-xs font-semibold">Filtrar Mapa</h4>
          {hasActive && (
            <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-bold">
              {activeCount}
            </span>
          )}
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Tipo filter */}
      <div className="mb-3">
        <label className="text-[11px] text-muted-foreground block mb-1">Tipo de Incidencia</label>
        <FilterCombobox
          label="tipo-mapa"
          value={local.tipo || ''}
          options={tipos}
          onChange={v => setLocal(p => ({ ...p, tipo: v }))}
          loading={isLoading}
          placeholder="Todos los tipos..."
          width="240px"
        />
      </div>

      {/* Date range */}
      <div className="mb-3">
        <label className="text-[11px] text-muted-foreground flex items-center gap-1 mb-1.5">
          <CalendarRange className="w-3 h-3" /> Rango de Fecha
        </label>
        <div className="grid grid-cols-2 gap-1.5">
          <div>
            <label className="text-[10px] text-muted-foreground block mb-0.5">Desde</label>
            <input
              type="date"
              value={local.date_from || ''}
              onChange={e => setLocal(p => ({ ...p, date_from: e.target.value }))}
              data-testid="map-date-from"
              className="w-full px-2 py-1.5 rounded-lg text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-0.5">Hasta</label>
            <input
              type="date"
              value={local.date_to || ''}
              onChange={e => setLocal(p => ({ ...p, date_to: e.target.value }))}
              data-testid="map-date-to"
              className="w-full px-2 py-1.5 rounded-lg text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" onClick={apply} className="flex-1 h-7 text-xs bg-[#008040] hover:bg-[#006633]" data-testid="map-filter-apply">
          Aplicar
        </Button>
        <Button size="sm" variant="outline" onClick={clear} disabled={!hasActive} className="h-7 text-xs" data-testid="map-filter-clear">
          Limpiar
        </Button>
      </div>

      {/* Active filters summary */}
      {hasActive && (
        <div className="mt-2.5 p-2 bg-primary/10 rounded-lg text-[11px] space-y-0.5">
          {local.tipo && <div><span className="font-medium">Tipo:</span> {local.tipo.length > 25 ? local.tipo.substring(0, 25) + '…' : local.tipo}</div>}
          {(local.date_from || local.date_to) && <div><span className="font-medium">Fecha:</span> {local.date_from || '…'} → {local.date_to || '…'}</div>}
        </div>
      )}
    </div>
  );
}
